import { Tag } from '@design-system/components/tag/tag';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';
import {
  faArrowsRotate,
  faFileLines,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton, Tooltip } from '@mui/material';
import { clsx } from 'clsx';
import { Menu } from '../view-models/library.viewMappers';
import { bgColorsVars } from '@design-system/vars.css';
import { GalleryItemMenu } from './GalleryItemMenu';
import { useGallerySelection } from '../hooks/useGallerySelection';
import { useGalleryVirtualization } from '../hooks/useGalleryVirtualization';

export type FilesGalleryItem = {
  id: string;
  imageUrl: string;
  name: string;
  tag: string;
  date: string;
  onClick: (multiselect: boolean) => void;
  duration: string;
  selected: boolean;
  description?: string;
  menu: Menu;
  isProcessing?: boolean;
};
export type FilesGallery = Array<FilesGalleryItem>;

type FilesGalleryViewProps = {
  data: FilesGallery;
  loading: boolean;
  clearSelection: () => void;
  filesGalleryRef: React.MutableRefObject<HTMLDivElement | null>;
  foldersGalleryRef: React.MutableRefObject<HTMLDivElement | null>;
};

export const FilesGalleryView = (p: FilesGalleryViewProps) => {
  if (p.loading) {
    return (
      <div className="flex flex-wrap gap-6 max-w-[1200px] items-center justify-center">
        {Array(8)
          .fill(0)
          .map((_, i) => (
            <Skeleton
              height={260}
              sx={{ backgroundColor: bgColorsVars['N-20'], minWidth: 240 }}
              key={i}
            />
          ))}
      </div>
    );
  }
  return <FilesGallery {...p} />;
};

const FilesGallery = ({
  data,
  clearSelection,
  filesGalleryRef,
  foldersGalleryRef,
}: FilesGalleryViewProps) => {
  const { multiselect } = useGallerySelection({
    clearSelection,
    filesGalleryRef,
    foldersGalleryRef,
  });
  const { viewData, loaderRef } = useGalleryVirtualization({ data });

  return (
    <div
      className="flex flex-col md:flex-row gap-6 flex-wrap items-center md:items-start"
      ref={filesGalleryRef}
    >
      {viewData.map((item) => (
        <div key={item.id} className="relative">
          {!item.isProcessing ? null : (
            <div
              className={clsx(
                'absolute w-full h-full z-10 flex items-center justify-center',
                sprinkles({ bg: 'W-100/50' }),
              )}
            >
              <Tag
                label="Processing"
                startIcon={faArrowsRotate}
                className={sprinkles({ bg: 'P-20' })}
                readonly
              />
            </div>
          )}
          <div
            className={clsx(
              'hover:shadow-galleryHover rounded-[12px] p-0 w-[260px]',
              item.selected && 'border-[2px] shadow-gallery',
              item.selected && sprinkles({ borderColor: 'P-80' }),
              !item.selected && 'p-0.5 border-none',
            )}
            onClick={() => {
              item.onClick(multiselect);
            }}
          >
            <div className="relative">
              <img
                src={item.imageUrl}
                className="w-[260px] h-[200px] object-contain bg-black rounded-t-[10px]"
              />
              {item.isProcessing ? null : <GalleryItemMenu menu={item.menu} />}
              <Typography
                className={clsx(
                  'absolute bottom-4 right-4 text-white p-0.5 rounded-[4px]',
                  sprinkles({ bg: 'B-100/50' }),
                )}
              >
                {item.duration}
              </Typography>
            </div>
            <div className="flex flex-col gap-2 bg-white p-4 pt-2 rounded-b-[10px] h-[84px] w-[256px]">
              <Typography
                variant="body"
                size="large"
                className="max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap"
              >
                {item.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography
                  className={clsx('min-w-[80px]', sprinkles({ color: 'N-60' }))}
                >
                  {item.date}
                </Typography>
                <Tag label={item.tag} readonly={true} />
                {!item.description ? null : (
                  <Tooltip
                    title={item.description}
                    placement="right-start"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'black',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 500,
                          fontFamily: 'inherit',
                          padding: '4px 8px',
                        },
                      },
                    }}
                  >
                    <div
                      className={clsx(
                        'flex items-center justify-center text-white rounded-[35px] w-[28px] h-[28px]',
                        sprinkles({
                          bg: { hover: 'B-100/50', lightMode: 'N-40' },
                        }),
                      )}
                    >
                      <FontAwesomeIcon icon={faFileLines} />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
      <div ref={loaderRef}>
        <Skeleton
          animation="wave"
          variant="rectangular"
          width={'100%'}
          height={100}
        />
      </div>
    </div>
  );
};
